import { useTranslation } from 'react-i18next';

import { Box, Button, Grid, Typography } from '@mui/material';

import { useBrands } from '@/hooks/useBrands';

import { FormikForm } from '@/components/formik/FormikForm';
import { FormikPhone } from '@/components/formik/FormikPhone';
import { FormikSelect } from '@/components/formik/FormikSelect';
import { FormikSubmit } from '@/components/formik/FormikSubmit';
import { FormikText } from '@/components/formik/FormikText';
import { Loader } from '@/components/Loader';
import { GenericModalFooter } from '@/components/modal/GenericModalFooter';
import type { TStepComponentProps } from '@/components/stepper/types';

import type { TCreateStationForm } from '@/types/station';

import { stationGeneralInfoSchema } from '@/validation/station';

import { styles as formStyles } from '../Form.styles';
import { FormikLocation } from './FormikLocation';
import { InteractiveMap } from './InteractiveMap';

export const GeneralInformation = ({
  data,
  moveBack,
  onSubmit,
}: TStepComponentProps<TCreateStationForm>) => {
  const { t } = useTranslation();

  const brands = useBrands(data.generalInfo?.brand);

  return (
    <FormikForm
      initialValues={data.generalInfo}
      validationSchema={stationGeneralInfoSchema}
      onSubmit={(generalInfo) => onSubmit({ generalInfo })}
      sx={formStyles.form}
    >
      <Loader active={brands.loading} position="absolute" />

      <Box sx={formStyles.content}>
        <Typography variant="h6" sx={formStyles.title}>
          {t('stationSetup.generalInfo.title')}
        </Typography>

        <FormikSelect name="brand" label="station.generalInfo.brand" options={brands.options} />
        <FormikText name="name" label="station.generalInfo.name" />

        <FormikLocation />
        <InteractiveMap />

        <Grid spacing={2} container>
          <Grid xs={12} laptop={6} item>
            <FormikText name="location.lat" type="number" label="formLabels.latitude" />
          </Grid>

          <Grid xs={12} laptop={6} item>
            <FormikText name="location.lon" type="number" label="formLabels.longitude" />
          </Grid>
        </Grid>

        <FormikPhone
          name="phone.0"
          autoComplete="tel"
          label="formLabels.phoneNumber"
          countrySelectComponent={() => null}
          international
        />

        <FormikText name="email.0" type="email" label="formLabels.email" />
      </Box>

      <GenericModalFooter sx={formStyles.footer} squareButtons>
        <Box sx={formStyles.controls}>
          <Button variant="outlined" onClick={moveBack}>
            {t('common.back')}
          </Button>

          <FormikSubmit>{t('common.next')}</FormikSubmit>
        </Box>
      </GenericModalFooter>
    </FormikForm>
  );
};
